/**
 * Phase 16 Plan 16-03 — ActivateWorkOrderDialog
 *
 * Confirms activation of a Draft Work Order (Draft → Active).
 * Prerequisites: ≥1 active machine + ≥1 KPI (Phase 17 stub; Phase 16 returns 409 for KPI = 0).
 * On 409 WO_ACTIVATION_BLOCKED, surfaces the backend message inline.
 */

import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useActivateWorkOrderMutation } from '@/hooks/useWorkOrderQueries';
import { toast } from '@/lib/toast';
import type { WorkOrderDetail } from '@/types/work-orders';

export interface ActivateWorkOrderDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (o: boolean) => void;
  readonly workOrder: WorkOrderDetail;
}

export function ActivateWorkOrderDialog({
  open,
  onOpenChange,
  workOrder,
}: ActivateWorkOrderDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mutation = useActivateWorkOrderMutation();

  function handleClose(): void {
    setErrorMessage(null);
    mutation.reset();
    onOpenChange(false);
  }

  function handleConfirm(): void {
    setErrorMessage(null);
    mutation.mutate(workOrder.id, {
      onSuccess: () => {
        toast.success(`Work Order ${workOrder.woNumber} activated`);
        onOpenChange(false);
      },
      onError: (err) => {
        const msg = err.detail ?? err.message;
        setErrorMessage(msg);
        toast.error('Activation failed', { description: msg });
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="activate-wo-dialog">
        <DialogHeader>
          <DialogTitle>Activate Work Order</DialogTitle>
          <DialogDescription>
            Activating <strong>{workOrder.woNumber}</strong> will move it from Draft to Active.
            Prerequisites:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2 text-sm">
          <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
            <li>At least 1 active machine must be linked.</li>
            <li>At least 1 KPI must be linked (managed in Phase 17).</li>
          </ul>
          {errorMessage !== null && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="activate-wo-dialog-error"
            >
              {errorMessage}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
            data-testid="activate-wo-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={mutation.isPending}
            data-testid="activate-wo-dialog-confirm"
          >
            {mutation.isPending ? 'Activating…' : 'Activate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
